import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import {
  StepViewModel,
  TestCaseSelectionsMap,
  VendorTestCaseSelectionsMap,
  WaterfallViewModel,
} from '../../models/waterfall';
import { IntegrationSettings } from '../../models/integrationSettings';
import { VoxTestCaseViewModel } from '../../models/voxTestCase';
import { useVoxTestCases } from '../../hooks/useVoxTestCases';
import { useWaterfallTestMode } from '../../hooks/useWaterfallTestMode';

interface WaterfallTestModePanelProps {
  waterfall: WaterfallViewModel;
  steps: StepViewModel[];
  integrationSettings: IntegrationSettings[];
}

interface FlattenedStep {
  stepGuid: string;
  label: string;
  vendors: string[];
}

interface TestCaseOption {
  /** Combined code + description so the dropdown filter matches either. */
  label: string;
  value: number;
  code: string;
  description: string;
  inactive: boolean;
}

/** step guid -> (vendor -> selected test case id) */
type SelectionsByStep = Record<string, Record<string, number | null>>;

/** vendor -> selected test case id (used for the operation-specific upgrade/refresh configs) */
type SelectionsByVendor = Record<string, number | null>;

const flattenSteps = (steps: StepViewModel[], integrationSettings: IntegrationSettings[]): FlattenedStep[] => {
  const vendorsForStep = (step: StepViewModel): string[] => {
    const vendors = new Set(
      (step.integrationSettingsIds ?? [])
        .map((id) => integrationSettings.find((setting) => setting.integrationSettingsId === id)?.vendor)
        .filter((vendor): vendor is string => !!vendor),
    );
    return [...vendors];
  };

  const result: FlattenedStep[] = [];
  const walk = (step: StepViewModel) => {
    result.push({
      stepGuid: step.stepGuid,
      label: step.description?.trim() || `Step ${step.stepGuid.slice(0, 8)}`,
      vendors: vendorsForStep(step),
    });
    step.nextSteps?.forEach(walk);
  };
  steps.forEach(walk);
  return result;
};

const buildSavedSelections = (
  selections: TestCaseSelectionsMap,
  testCases: VoxTestCaseViewModel[],
): SelectionsByStep => {
  const caseById = new Map(testCases.map((testCase) => [testCase.voxTestCaseId, testCase]));
  const result: SelectionsByStep = {};
  Object.entries(selections ?? {}).forEach(([stepGuid, ids]) => {
    const byVendor: Record<string, number | null> = {};
    (ids ?? []).forEach((id) => {
      const vendor = caseById.get(id)?.vendor;
      if (vendor) byVendor[vendor] = id;
    });
    result[stepGuid] = byVendor;
  });
  return result;
};

const collectStepGuids = (steps: StepViewModel[]): Set<string> => {
  const guids = new Set<string>();
  const walk = (step: StepViewModel) => {
    guids.add(step.stepGuid);
    step.nextSteps?.forEach(walk);
  };
  steps.forEach(walk);
  return guids;
};

const toSelectionsMap = (
  selectionsByStep: SelectionsByStep,
  validStepGuids: Set<string>,
): TestCaseSelectionsMap => {
  const result: TestCaseSelectionsMap = {};
  Object.entries(selectionsByStep).forEach(([stepGuid, byVendor]) => {
    if (!validStepGuids.has(stepGuid)) return;
    const ids = Object.values(byVendor).filter((id): id is number => id != null);
    if (ids.length > 0) result[stepGuid] = ids;
  });
  return result;
};

const toVendorMap = (selectionsByVendor: SelectionsByVendor): VendorTestCaseSelectionsMap => {
  const result: VendorTestCaseSelectionsMap = {};
  Object.entries(selectionsByVendor).forEach(([vendor, id]) => {
    if (id != null) result[vendor] = id;
  });
  return result;
};

const buildVendorSelections = (selections: VendorTestCaseSelectionsMap): SelectionsByVendor => {
  const result: SelectionsByVendor = {};
  Object.entries(selections ?? {}).forEach(([vendor, id]) => {
    result[vendor] = id;
  });
  return result;
};

export const WaterfallTestModePanel: FC<WaterfallTestModePanelProps> = ({ waterfall, steps, integrationSettings }) => {
  const toastRef = useRef<Toast>(null);
  // Load the full catalog (including inactive) so saved selections that reference a
  // now-inactive case can still be mapped and preserved; only active cases are offered
  // as new choices below.
  const { data, isLoading } = useVoxTestCases(undefined, false);
  const testCases = useMemo(() => data ?? [], [data]);
  const { setTestMode, isSettingTestMode } = useWaterfallTestMode(waterfall.waterfallId);

  const [expanded, setExpanded] = useState<boolean>(waterfall.isTestMode);
  const [selectionsByStep, setSelectionsByStep] = useState<SelectionsByStep>({});
  const [upgradeSelections, setUpgradeSelections] = useState<SelectionsByVendor>({});
  const [refreshSelections, setRefreshSelections] = useState<SelectionsByVendor>({});

  const flattenedSteps = useMemo(
    () => flattenSteps(steps, integrationSettings).filter((step) => step.vendors.length > 0),
    [steps, integrationSettings],
  );

  // Vendors this waterfall actually calls (union across steps); the upgrade/refresh configs
  // offer one dropdown per relevant vendor rather than the whole IntegrationVendor enum.
  const relevantVendors = useMemo(
    () => [...new Set(flattenedSteps.flatMap((step) => step.vendors))].sort(),
    [flattenedSteps],
  );

  // Sync the editable selections from the persisted waterfall once the global test cases
  // are loaded (their vendors are needed to map saved ids back to their vendor slot).
  useEffect(() => {
    setSelectionsByStep(buildSavedSelections(waterfall.testCaseSelections, testCases));
  }, [waterfall.testCaseSelections, testCases]);

  useEffect(() => {
    setUpgradeSelections(buildVendorSelections(waterfall.upgradeTestCaseSelections));
  }, [waterfall.upgradeTestCaseSelections]);

  useEffect(() => {
    setRefreshSelections(buildVendorSelections(waterfall.refreshTestCaseSelections));
  }, [waterfall.refreshTestCaseSelections]);

  const casesByVendor = useMemo(() => {
    const map = new Map<string, VoxTestCaseViewModel[]>();
    testCases.forEach((testCase) => {
      const list = map.get(testCase.vendor) ?? [];
      list.push(testCase);
      map.set(testCase.vendor, list);
    });
    return map;
  }, [testCases]);

  const caseById = useMemo(
    () => new Map(testCases.map((testCase) => [testCase.voxTestCaseId, testCase])),
    [testCases],
  );

  const validStepGuids = useMemo(() => collectStepGuids(steps), [steps]);

  const showError = (detail: string) =>
    toastRef.current?.show({ severity: 'error', summary: 'Error', detail, life: 6000 });
  const showSuccess = (detail: string) =>
    toastRef.current?.show({ severity: 'success', summary: 'Success', detail, life: 3000 });

  const persist = async (
    isTestMode: boolean,
    stepSelections: SelectionsByStep,
    upgrade: SelectionsByVendor,
    refresh: SelectionsByVendor,
  ) => {
    await setTestMode({
      isTestMode,
      testCaseSelections: toSelectionsMap(stepSelections, validStepGuids),
      upgradeTestCaseSelections: toVendorMap(upgrade),
      refreshTestCaseSelections: toVendorMap(refresh),
    });
  };

  const handleToggleTestMode = async (checked: boolean) => {
    setExpanded(checked);
    if (!checked && !waterfall.isTestMode) return;
    try {
      await persist(checked, selectionsByStep, upgradeSelections, refreshSelections);
      showSuccess(checked ? 'Test mode enabled' : 'Test mode disabled');
    } catch (error: any) {
      setExpanded(!checked);
      showError(error?.response?.data?.errors || error?.message || 'Failed to update test mode');
    }
  };

  const handleSelect = async (stepGuid: string, vendor: string, testCaseId: number | null) => {
    const previous = selectionsByStep;
    const next: SelectionsByStep = {
      ...selectionsByStep,
      [stepGuid]: { ...(selectionsByStep[stepGuid] ?? {}), [vendor]: testCaseId },
    };
    setSelectionsByStep(next);
    if (!waterfall.isTestMode) return;
    try {
      await persist(true, next, upgradeSelections, refreshSelections);
      showSuccess('Test case selection updated');
    } catch (error: any) {
      setSelectionsByStep(previous);
      showError(error?.response?.data?.errors || error?.message || 'Failed to update selection');
    }
  };

  const handleVendorSelect = async (
    operation: 'upgrade' | 'refresh',
    vendor: string,
    testCaseId: number | null,
  ) => {
    const isUpgrade = operation === 'upgrade';
    const previous = isUpgrade ? upgradeSelections : refreshSelections;
    const next: SelectionsByVendor = { ...previous, [vendor]: testCaseId };
    const setState = isUpgrade ? setUpgradeSelections : setRefreshSelections;
    setState(next);
    if (!waterfall.isTestMode) return;
    try {
      await persist(
        true,
        selectionsByStep,
        isUpgrade ? next : upgradeSelections,
        isUpgrade ? refreshSelections : next,
      );
      showSuccess('Test case selection updated');
    } catch (error: any) {
      setState(previous);
      showError(error?.response?.data?.errors || error?.message || 'Failed to update selection');
    }
  };

  const toOption = (testCase: VoxTestCaseViewModel, inactive: boolean): TestCaseOption => {
    const description = testCase.description?.trim() ?? '';
    return {
      label: description ? `${testCase.name} ${description}` : testCase.name,
      value: testCase.voxTestCaseId,
      code: testCase.name,
      description,
      inactive,
    };
  };

  const optionsForVendor = (vendor: string, selectedId: number | null): TestCaseOption[] => {
    const options = (casesByVendor.get(vendor) ?? [])
      .filter((testCase) => testCase.active)
      .map((testCase) => toOption(testCase, false));
    // Keep a currently-selected but now-inactive case visible so it isn't silently lost.
    if (selectedId != null && !options.some((option) => option.value === selectedId)) {
      const selected = caseById.get(selectedId);
      if (selected) options.unshift(toOption(selected, true));
    }
    return options;
  };

  const optionItemTemplate = (option: TestCaseOption) => (
    <div className="flex flex-column gap-1 py-1">
      <span className="font-medium text-sm">
        {option.code}
        {option.inactive && ' (inactive)'}
      </span>
      {option.description && <span className="text-xs text-gray-500 white-space-normal">{option.description}</span>}
    </div>
  );

  const selectedValueTemplate = (option: TestCaseOption | null, props: DropdownProps) => {
    if (!option) return <span className="text-gray-500">{props.placeholder}</span>;
    return (
      <div className="flex flex-column">
        <span className="text-sm">
          {option.code}
          {option.inactive && ' (inactive)'}
        </span>
        {option.description && (
          <span className="text-xs text-gray-500 white-space-nowrap overflow-hidden text-overflow-ellipsis">
            {option.description}
          </span>
        )}
      </div>
    );
  };

  const testCaseDropdown = (vendor: string, selectedId: number | null, onChange: (id: number | null) => void) => (
    <Dropdown
      value={selectedId}
      options={optionsForVendor(vendor, selectedId)}
      optionLabel="label"
      optionValue="value"
      itemTemplate={optionItemTemplate}
      valueTemplate={selectedValueTemplate}
      filter
      filterBy="label"
      filterPlaceholder="Search by code or description"
      resetFilterOnHide
      placeholder="Select a test case"
      className="w-24rem"
      panelClassName="w-24rem"
      onChange={(e) => onChange(e.value ?? null)}
      showClear
      disabled={isSettingTestMode}
      emptyMessage="No test cases for this vendor"
      appendTo={document.body}
    />
  );

  const renderVendorSection = (
    title: string,
    description: string,
    selections: SelectionsByVendor,
    operation: 'upgrade' | 'refresh',
  ) => (
    <div className="p-3 border border-gray-300 rounded bg-white">
      <div className="font-medium text-sm text-gray-700">{title}</div>
      <small className="text-gray-500 block mb-2">{description}</small>
      {relevantVendors.length === 0 ? (
        <div className="text-gray-500 text-sm italic">This waterfall version has no steps with a configured vendor.</div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {relevantVendors.map((vendor) => (
            <div key={vendor} className="field m-0">
              <label className="text-xs text-gray-500 block mb-1">{vendor}</label>
              {testCaseDropdown(vendor, selections[vendor] ?? null, (id) => handleVendorSelect(operation, vendor, id))}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <Toast ref={toastRef} />

      <div className="flex align-items-center justify-content-between">
        <div>
          <div className="flex align-items-center gap-2">
            <h3 className="text-lg font-medium m-0">Test Mode</h3>
            {waterfall.isTestMode && <Tag value="Active" severity="warning" />}
          </div>
          <small className="text-gray-500">
            Apply a saved VoX test case per step and vendor before the vendor call. Manage test cases under VoX Test
            Cases.
          </small>
        </div>
        <InputSwitch
          checked={expanded}
          onChange={(e) => handleToggleTestMode(e.value ?? false)}
          disabled={isSettingTestMode || isLoading}
          aria-label="Enable test mode"
        />
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Message
            severity="warn"
            className="w-full mb-4"
            text="Test mode modifies every real request to this waterfall before the vendor call, applying the selected test case's override values for the vendor chosen at each step. Use with caution on live tenants."
          />

          {isLoading ? (
            <div className="text-gray-500 text-sm italic">Loading test cases...</div>
          ) : flattenedSteps.length === 0 ? (
            <div className="text-gray-500 text-sm italic p-3 border border-dashed border-gray-300 rounded">
              This waterfall version has no steps with a configured vendor.
            </div>
          ) : (
            <div className="flex flex-column gap-4">
              <div className="flex flex-column gap-3">
                <div className="font-semibold text-sm text-gray-800">Waterfall step test cases</div>
                {flattenedSteps.map((step) => (
                  <div key={step.stepGuid} className="p-3 border border-gray-300 rounded bg-white">
                    <div className="font-medium text-sm text-gray-700 mb-2">{step.label}</div>
                    <div className="flex flex-wrap gap-3">
                      {step.vendors.map((vendor) => (
                        <div key={vendor} className="field m-0">
                          <label className="text-xs text-gray-500 block mb-1">{vendor}</label>
                          {testCaseDropdown(
                            vendor,
                            selectionsByStep[step.stepGuid]?.[vendor] ?? null,
                            (id) => handleSelect(step.stepGuid, vendor, id),
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-column gap-3 pt-3 border-t border-gray-200">
                <div className="font-semibold text-sm text-gray-800">Upgrade &amp; refresh test cases</div>
                <small className="text-gray-500 -mt-2">
                  These override the vendor request when an order on this waterfall is upgraded or refreshed. They are
                  independent of the per-step selections above.
                </small>
                {renderVendorSection(
                  'Upgrade',
                  'Applied when an order created by this waterfall is upgraded, matched on the upgrade vendor.',
                  upgradeSelections,
                  'upgrade',
                )}
                {renderVendorSection(
                  'Refresh',
                  'Applied when an order created by this waterfall is refreshed, matched on the refresh vendor.',
                  refreshSelections,
                  'refresh',
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
